import './PremiumHeroSlider.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useCallback, useRef } from 'react';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { selectSliderItems, selectSliderStatus, fetchSliderItems } from '../../redux/sliderSlice';

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0
  })
};


const PremiumHeroSlider = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const [direction, setDirection] = useState(0);
  const slides = useSelector(selectSliderItems);
  const status = useSelector(selectSliderStatus);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchSliderItems());
    }
  }, [status, dispatch]);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % slides.length);
    resetTimeout();
  }, [slides.length, resetTimeout]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    resetTimeout();
  }, [slides.length, resetTimeout]);

  // Autoplay
  useEffect(() => {
    if (slides.length > 0) {
      resetTimeout();
      timeoutRef.current = setTimeout(handleNext, 6000);
    }
    return () => resetTimeout();
  }, [currentIndex, slides.length, handleNext, resetTimeout]);

  if (slides.length === 0) return null;

  const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
  const nextIndex = (currentIndex + 1) % slides.length;

  const activeSlide = slides[currentIndex];
  const prevSlide = slides[prevIndex];
  const nextSlide = slides[nextIndex];

  return (
    <Box className="premium-hero-slider">
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          className="triplet-scene"
          transition={{
            x: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
            opacity: { duration: 0.6 }
          }}
        >
          {/* Previous Preview */}
          <Box className="triplet-side triplet-prev">
            <img src={prevSlide.imageUrl} alt="Previous" />
            <div className="triplet-mask" />
          </Box>

          {/* Active Focus */}
          <div className="triplet-active">
            <Box className="triplet-image-container">
              <img src={activeSlide.imageUrl} alt={activeSlide.title} />

              {(activeSlide.subtitle?.length > 1 || activeSlide?.title?.length > 1) && (
                <Box className="slide-overlay">
                  <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                  >
                    <Typography className="hero-title">
                      {activeSlide?.title?.length > 1 ? activeSlide.title : activeSlide.subtitle}
                    </Typography>
                  </motion.div>
                  {activeSlide?.title?.length > 1 && (
                    <motion.div
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                    >
                      <Typography className="hero-subtitle">
                        {activeSlide.subtitle}
                      </Typography>
                    </motion.div>
                  )}
                </Box>
              )}
            </Box>
          </div>

          {/* Next Preview */}
          <Box className="triplet-side triplet-next">
            <img src={nextSlide.imageUrl} alt="Next" />
            <div className="triplet-mask" />
          </Box>
        </motion.div>
      </AnimatePresence>

      {/* Manual Navigation */}
      <div className="premium-nav-prev" onClick={handlePrev}>
        <KeyboardArrowLeft fontSize="large" />
      </div>
      <div className="premium-nav-next" onClick={handleNext}>
        <KeyboardArrowRight fontSize="large" />
      </div>
    </Box>
  );
};

export default PremiumHeroSlider;
